import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

export interface ISurfaceRow<T = number> {
  surfaceTotalCount: T;
  activeTotalCount: T;
  utilization: string;
  sprinterTotalCount: T;
  sprinterTwoYO: T;
  sprinterThreeYO: T;
  sprinterFourUpYO: T;
  routeTotalCount: T;
  routeTwoYO: T;
  routeThreeYO: T;
  routeFourUpYO: T;
}

export interface ISurfaceData {
  type: ESurfaceType;
  data: ISurfaceRow | ISurfaceRow<string>;
}

export interface IRowData {
  backgroundClassName: string;
  totalCount: number;
  surfaces: ISurfaceData[];
}

interface ICheckSurfaceResult {
  isDirt: boolean;
  isTurfAndSynthetic: boolean;
}

export enum ESurfaceType {
  DIRT = "Dirt",
  TURF_AND_SYNTHETIC = "Turf & Synthetic"
}

export const surfaceTypes = {
  dirt: {
    title: ESurfaceType.DIRT,
    dataField: "dirtSurfaceGroup"
  },
  turfAndSynthetic: {
    title: ESurfaceType.TURF_AND_SYNTHETIC,
    dataField: "turfSyntheticSurfaceGroup"
  }
};

const initialSurfaceGrandTotals: ISurfaceRow = {
  surfaceTotalCount: 0,
  activeTotalCount: 0,
  utilization: "",
  sprinterTotalCount: 0,
  sprinterTwoYO: 0,
  sprinterThreeYO: 0,
  sprinterFourUpYO: 0,
  routeTotalCount: 0,
  routeTwoYO: 0,
  routeThreeYO: 0,
  routeFourUpYO: 0
};

const countPercentages = (totalNumber: number, partNumber: number): string => {
  let result = 0;

  if (totalNumber && partNumber) {
    result = Math.round((partNumber / totalNumber) * 100);
  }

  return `${result}%`;
};

const getCorrectNumber = (number?: number): number => number || 0;

export const parseInventoryAnalyticsClass = (
  raceClass: racehorse360.RaceClass
): string => {
  switch (raceClass) {
    case racehorse360.RaceClass.RACE_CLASS_STAKES:
      return "Stakes Horses";
    case racehorse360.RaceClass.RACE_CLASS_ALLOWANCE:
      return "Allowance";
    case racehorse360.RaceClass.RACE_CLASS_STARTER:
      return "Starter";
    case racehorse360.RaceClass.RACE_CLASS_OPEN_CLAMING:
      return "Claiming";
    case racehorse360.RaceClass.RACE_CLASS_CLAMING_CONDITION:
      return "Claiming Condition";
    case racehorse360.RaceClass.RACE_CLASS_MAIDEN:
      return "Maiden";
    case racehorse360.RaceClass.RACE_CLASS_NO_STARTS:
      return "No starts";
    default:
      return "Invalid Class";
  }
};

export const getSurfaceContentRowModel = (
  data: racehorse360.IInventoryAnalyticsRaceSubclass,
  field: string
): ISurfaceRow => ({
  surfaceTotalCount: getCorrectNumber(data[field]?.totalCount),
  activeTotalCount: getCorrectNumber(data[field]?.activeCount),
  utilization: countPercentages(
    data[field]?.totalCount,
    data[field]?.activeCount
  ),
  sprinterTotalCount: getCorrectNumber(data[field]?.sprinter.totalCount),
  sprinterTwoYO: getCorrectNumber(data[field]?.sprinter.twoYearOlds),
  sprinterThreeYO: getCorrectNumber(data[field]?.sprinter.threeYearOlds),
  sprinterFourUpYO: getCorrectNumber(data[field]?.sprinter.fourAndUpYearOlds),
  routeTotalCount: getCorrectNumber(data[field]?.route.totalCount),
  routeTwoYO: getCorrectNumber(data[field]?.route.twoYearOlds),
  routeThreeYO: getCorrectNumber(data[field]?.route.threeYearOlds),
  routeFourUpYO: getCorrectNumber(data[field]?.route.fourAndUpYearOlds)
});

export const getSurfaceGrandTotals = (
  subClasses: racehorse360.IInventoryAnalyticsRaceSubclass[],
  field: string
): ISurfaceRow => {
  return subClasses.reduce((acc, cur) => {
    return {
      surfaceTotalCount:
        acc.surfaceTotalCount + getCorrectNumber(cur[field]?.totalCount),
      activeTotalCount:
        acc.activeTotalCount + getCorrectNumber(cur[field]?.activeCount),
      utilization: null,
      sprinterTotalCount:
        acc.sprinterTotalCount +
        getCorrectNumber(cur[field]?.sprinter.totalCount),
      sprinterTwoYO:
        acc.sprinterTwoYO + getCorrectNumber(cur[field]?.sprinter.twoYearOlds),
      sprinterThreeYO:
        acc.sprinterThreeYO +
        getCorrectNumber(cur[field]?.sprinter.threeYearOlds),
      sprinterFourUpYO:
        acc.sprinterFourUpYO +
        getCorrectNumber(cur[field]?.sprinter.fourAndUpYearOlds),
      routeTotalCount:
        acc.routeTotalCount + getCorrectNumber(cur[field]?.route.totalCount),
      routeTwoYO:
        acc.routeTwoYO + getCorrectNumber(cur[field]?.route.twoYearOlds),
      routeThreeYO:
        acc.routeThreeYO + getCorrectNumber(cur[field]?.route.threeYearOlds),
      routeFourUpYO:
        acc.routeFourUpYO +
        getCorrectNumber(cur[field]?.route.fourAndUpYearOlds)
    };
  }, initialSurfaceGrandTotals);
};

export const getPercentagesRow = (
  grandTotalsRow: ISurfaceRow,
  totalHorsesCount: number
): ISurfaceRow<string> => ({
  surfaceTotalCount: countPercentages(
    totalHorsesCount,
    grandTotalsRow.surfaceTotalCount
  ),
  activeTotalCount: countPercentages(
    grandTotalsRow.surfaceTotalCount,
    grandTotalsRow.activeTotalCount
  ),
  utilization: null,
  sprinterTotalCount: countPercentages(
    grandTotalsRow.surfaceTotalCount,
    grandTotalsRow.sprinterTotalCount
  ),
  sprinterTwoYO: null,
  sprinterThreeYO: null,
  sprinterFourUpYO: null,
  routeTotalCount: countPercentages(
    grandTotalsRow.surfaceTotalCount,
    grandTotalsRow.routeTotalCount
  ),
  routeTwoYO: null,
  routeThreeYO: null,
  routeFourUpYO: null
});

export const getBackgroundColor = (
  index: number,
  classes: { [key: string]: string }
): string => {
  const remainder = index % 3;

  switch (remainder) {
    case 0: {
      return classes.lightGreyCells;
    }
    case 1: {
      return classes.mediumGreyCells;
    }
    case 2: {
      return classes.darkGreyCells;
    }
  }
};

export const checkSurfaceType = (
  surface: ESurfaceType
): ICheckSurfaceResult => {
  return {
    isDirt: surface === ESurfaceType.DIRT,
    isTurfAndSynthetic: surface === ESurfaceType.TURF_AND_SYNTHETIC
  };
};
